<template>
  <BModal
    v-model="showModal"
    title="Import License Order"
    centered
    lazy
    no-footer
    size="xl"
    @hidden="close"
    @hide="hideModal"
  >
    <div class="modal-body p-0">
      <div class="row">
        <div class="col-12">
          <div class="row mb-3">
            <div class="col-12">
              <p class="mb-0 flex-grow-1">
                If you have bought license tickets from your Phoenix Contact
                sales subsidiary and you received a license-order xml file, you
                can import the licenses via an upload here.
              </p>
            </div>
          </div>
          <FormWithValidation
            :key="formKey"
            ref="formRef"
            v-model="licenseOrderXmlForm"
            col-class="col-12"
            :initial-state="licenseOrderXmlFormInitialState"
            :validation-rules-function="
              () => getLicenseOrderXmlFormRules(licenseOrderXmlForm)
            "
            :validation-messages="validationMessages"
            :field-labels-map="fieldLabelsMap"
            :saved-callback="importTicketIds"
            save-button-text="Add"
          >
            <template #xmlFile="slotProps">
              <FormField
                id="xmlFile"
                name="xmlFile"
                label="License Order XML File"
                :mandatory="true"
                :v-field="slotProps.vForm.xmlFile"
                :validation-messages="slotProps.validationMessages.xmlFile"
                :field-errors="slotProps.fieldErrors"
                :class="slotProps.getIsInvalidClass(slotProps.vForm.xmlFile)"
              >
                <UploadField
                  id="xmlFile"
                  v-model="licenseOrderXmlForm.xmlFile"
                  accept=".xml"
                  name="xmlFile"
                  :class="slotProps.getIsInvalidClass(slotProps.vForm.xmlFile)"
                />
              </FormField>
            </template>

            <template #importForCompany="slotProps">
              <FormField
                id="importForCompany"
                name="importForCompany"
                label="Import as company licenses"
                :v-field="slotProps.vForm.importForCompany"
                :validation-messages="
                  slotProps.validationMessages.importForCompany
                "
                :field-errors="slotProps.fieldErrors"
                :class="
                  slotProps.getIsInvalidClass(slotProps.vForm.importForCompany)
                "
              >
                <CheckBoxField
                  id="importForCompany"
                  v-model="licenseOrderXmlForm.importForCompany"
                  :class="
                    slotProps.getIsInvalidClass(
                      slotProps.vForm.importForCompany,
                    )
                  "
                  name="importForCompany"
                  class=""
                />
              </FormField>
            </template>

            <template #cancel-button>
              <StoreButton @click="close">
                <span>Cancel</span>
              </StoreButton>
            </template>
          </FormWithValidation>

          <div v-if="webModelGetLicenseImportResult">
            <div class="row mt-5">
              <div class="col-12">
                <p>
                  Successfully imported
                  {{
                    webModelGetLicenseImportResult.importedLicenses?.length || 0
                  }}
                  licenses.
                </p>
              </div>
            </div>
            <div
              v-if="
                webModelGetLicenseImportResult.ticketIdsWithErrors &&
                webModelGetLicenseImportResult.ticketIdsWithErrors.length > 0
              "
              class="row"
            >
              <div class="col-12">
                <p>Failed to import the following licenses:</p>
                <ul>
                  <li
                    v-for="ticketId in webModelGetLicenseImportResult.ticketIdsWithErrors"
                    :key="ticketId"
                  >
                    {{ ticketId }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BModal>
</template>

<script lang="ts" setup>
import { BModal } from "bootstrap-vue-next";
import FormWithValidation from "~/components/shared/forms/FormWithValidation.vue";
import FormField from "~/components/shared/forms/FormField.vue";
import { useUiStore } from "~/stores/ui/uistore";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";
import UploadField from "~/components/shared/inputfields/UploadField.vue";
import {
  type LicenseOrderXmlForm,
  getLicenseOrderXmlFormRules,
  validationMessages,
  fieldLabelsMap,
} from "~/validations/license-order-xml";
import CheckBoxField from "~/components/shared/inputfields/CheckBoxField.vue";
import type { WebModelGetLicenseImportResult } from "~/model/api/pLCnextStoreFrontendAPI.schemas";

const props = defineProps({
  callbackFunction: { type: Function, required: true },
});

const showModal = defineModel<boolean>({ default: false });
const formKey = ref(0);
const formRef = ref<{ resetForm: () => void } | null>(null);

const webModelGetLicenseImportResult = ref<
  WebModelGetLicenseImportResult | undefined
>(undefined);

function close() {
  reset();
  showModal.value = false;
}

function reset() {
  licenseOrderXmlForm.xmlFile = undefined;
  licenseOrderXmlFormInitialState.xmlFile = undefined;
  formKey.value += 1; // vue rerender so that everything is reset
  webModelGetLicenseImportResult.value = undefined;
}

const licenseOrderXmlFormInitialState = reactive({
  xmlFile: undefined,
  importForCompany: false,
});

const licenseOrderXmlForm = reactive<LicenseOrderXmlForm>({
  ...licenseOrderXmlFormInitialState,
});

const hideModal = () => {
  formRef.value?.resetForm();
  useUiStore().isFormDirty = false;
};

const importTicketIds = async (): Promise<void> => {
  const data: false | WebModelGetLicenseImportResult =
    await props.callbackFunction(licenseOrderXmlForm);
  if (data) {
    webModelGetLicenseImportResult.value = data;
    formRef.value?.resetForm();
  }
};
</script>
<!-- scoped - does not work here because the bootstrap modal uses teleports -->
<style lang="scss"></style>
